//#region Import

import { DicomBox, Apps } from "../DicomManager";
import {
  ReportApps,
  getDwvPos,
  convertDwvPosition,
  getXAngle,
  getOppositePoint,
  getImagesObjFromDwvPosition,
} from "./DicomReports";
import { jsPDF } from "jspdf";
import {
  showLoader,
  hideLoader,
  delay,
} from "@/components/controls/Loader.vue";
import { getFormattedDate } from "@/components/reports/fileMethods";
import swal from "sweetalert";
import { drawBox, drawLine, drawPoint } from "@/components/draw/draw";
import DicomCutReportDlg from "./DicomCutReportDlg.vue";
import ReportsListDlg from "./ReportsListDlg.vue";
import {
  getCrossPointsForPlane,
  toBufferGeometry,
} from "@/components/geometry/BhvHelper.js";
import {
  getImgDataForCurrentView,
  getPatientData,
  createPdfHeader,
} from "@/components/reports/ReportsManager";
import html2canvas from "html2canvas";
import { degreesToRadians } from "@/components/geometry/geometry.js";
import {
  DEFAULT_BONES_TYPE,
  SOLID_BONES_TYPE,
  FULL_BONES_TYPE,
  switchTeethRoots,
  snapMandibleToOcclusion,
  switchBonesType,
} from "@/components/controls/TeethListManager";
import {
  saveCutReportSettings,
  saveCutReportPdfInS3,
  sendGetCutReportsRequest,
  sendRemovePdfReportRequest,
} from "@/api/reportApi";

import { drawPointsArray } from "@/components/geometry/NumericHelper.js";

import Konva from "konva";
let dwv = require("dwv");

//#endregion

//#region Fields

let controlsInitialized = false;

let isFirstGenerate = true;

let cutObjects = [];
const extensionLength = 15;
const maxApexOffset = 12;
const minBoneJawWidth = 5;
const maxBoneDistance = 25;
let mandilaBox = null;
let leftMandBox = null;
let rightMandBox = null;
let maxillaBox = null;
let leftMaxBox = null;
let rightMaxBox = null;
let fullMaxillaBox = null;
let fullMandilaBox = null;

let maxCutY = null;
let mandCutY = null;

let axialSpacing = null; //geometry.getSpacing().get(0)
let dwvOrigin = null;
let dwvController = null;

const pointsMaterial = new THREE.PointsMaterial({
  color: 0x0000ff,
  size: 0.25, // Adjust point size
});
const lineMaterial = new THREE.LineBasicMaterial({
  color: 0x0000ff,
});
const lineMaterial2 = new THREE.LineBasicMaterial({
  color: 0xffff00,
});
const meshMaterial = new THREE.MeshBasicMaterial({
  color: 0x0000ff,
  //   wireframe: true,
});

let cutRaycaster = new THREE.Raycaster(
  new THREE.Vector3(),
  new THREE.Vector3(),
); //   , 0.1,        100000

let lastGeneratedPdf = null;

const nerveLineLogics = [
  {
    index: 0,
    text: "Nearest (90°)",
  },
  {
    index: 1,
    text: "Middle",
  },
  {
    index: 2,
    text: "Independent",
  },
];

const dentations = [
  {
    isUpper: true,
    text: "Maxillary",
    imageUrl: null,
  },
  {
    isUpper: false,
    text: "Mandibular",
    imageUrl: null,
  },
];

const lineFrequencies = [
  {
    value: 1,
    text: "1st image",
  },
  {
    value: 2,
    text: "2nd image",
  },
  {
    value: 3,
    text: "3rd image",
  },
];

//#endregion

//#region Context

const cutContext = {
  cutStart: 0,
  cutEnd: 0,
  cutMin: 0,
  cutMax: 0,
  cutLength: 0,
  cutStep: 1,
  sliceInterval: 1,
  sliceIntervals: [1, 1.5, 2],
  lineFrequencies: lineFrequencies,
  lineFrequency: lineFrequencies[0],
  dentation: dentations[1],
  dentations: dentations,
  nerveLineLogic: nerveLineLogics[0],
  nerveLineLogics: nerveLineLogics,
  cutImages: [],
  savedCutImages: [],
  panoramicWidth: null,
  panoramicHeight: null,
  imagesPerLine: 6,
  showLines: true,
  panoOnEachPage: false,
  pano3dSideBySide: false,
  showTopControls: true,
  sharpImageValue: 0,

  imgHeight: 60,
  nerveColor: "#FF0000",
  measureColor: "#FFFF00",
  widthColor: "#00FFFF",
  backgroundColor: "#000000", //"#FFFFFF",

  panoWidth: 0,

  selectedImage: null,
  selectedStage: null,
  selectedLayer: null,
  clickPoint: null,

  pdfChanged: true,

  reportData: {
    reportName: "",
    isCompanyLogo: true,
    isPatientInfo: true,
    isCaseLink: true,
    isQrCode: true,
    linkExpiration: 0,
    isLinkReadOnly: false,
    isLinkResetCase: false,
    isLinkAllowDownloads: false,
    isLinkAnonymized: true,
  },

  savedCutReports: [],
};

let globalKonvaStage = null;
let globalKonvaLayer = null;

//#endregion

//#region Init

async function fetchCoordMappings(
  coordMappingMaxillaUrl,
  coordMappingMandibleUrl,
) {
  console.log(
    "fetchCoordMappings",
    coordMappingMaxillaUrl,
    coordMappingMandibleUrl,
  );
  if (!cutObjects || !cutObjects.length) {
    maxillaBox = getMeshesBox(true);
    mandilaBox = getMeshesBox(false);
    maxCutY = maxillaBox.center.y;
    mandCutY = mandilaBox.center.y;

    fullMaxillaBox = getMeshesBox(true, true);
    fullMandilaBox = getMeshesBox(false, true);
    leftMandBox = getHalfBox(fullMandilaBox, true);
    rightMandBox = getHalfBox(fullMandilaBox, false);
    leftMaxBox = getHalfBox(fullMaxillaBox, true); //drawBox(leftMaxBox, 0x0000ff);
    rightMaxBox = getHalfBox(fullMaxillaBox, false); //drawBox(rightMaxBox, 0x00ff00);

    const app = ReportApps[0];
    const layerGroup = app.getLayerGroupByDivId(app.divId);
    const viewLayer = layerGroup.getActiveViewLayer();
    const appGeometry = app.geometry;
    dwvOrigin = appGeometry.getOrigin();
    dwvController = viewLayer.getViewController();
    axialSpacing = appGeometry.getSpacing().get(0);

    let maxillaObjects = await getCutObjectsFromCoordJson(
      coordMappingMaxillaUrl,
      true,
    );
    let mandibleObjects = await getCutObjectsFromCoordJson(
      coordMappingMandibleUrl,
      false,
    );

    cutObjects = maxillaObjects.concat(mandibleObjects);
  }
}

async function getCutObjectsFromCoordJson(coordUrl, isMaxilla) {
  let retCutObjects = [];
  try {
    let response = await fetch(coordUrl, {
      method: "GET",
      cache: "no-cache",
    });
    if (response.ok) {
      const coordJson = await response.json();
      const trajArr = coordJson.trajectory;
      const derivArr = coordJson.trajectory_deriv;
      const rot_centers = coordJson.rot_centers;
      const rot_vectors = coordJson.rot_vectors;
      const rotationExist =
        rot_centers && rot_centers.length && rot_vectors && rot_vectors.length;
      const rot_centers_x = rotationExist ? rot_centers[2][0][0] : null;
      const rot_centers_y = rotationExist ? rot_centers[1][0][0] : null;
      const rot_centers_z = rotationExist ? rot_centers[0][0][0] : null;

      const trajPoints = trajArr.map((t) => new THREE.Vector2(t[0], t[1]));
      const trajLength = trajPoints.reduce((acc, current, i, arr) => {
        return i > 0 ? acc + arr[i - 1].distanceTo(current) : acc;
      }, 0);
      const coordLength = trajArr.length;
      console.log(
        "getCutObjectsFromCoordJson 1",
        trajPoints.length,
        trajLength,
      );

      let dicomBox = DicomBox;
      if (dicomBox) {
        let cut3dLength = 0;

        for (let i = 0; i < coordLength; i++) {
          const cutCoord = new THREE.Vector3(trajArr[i][0], trajArr[i][1], 0);
          const cutWorldPos = dwvController.convertPlanePosToWorldPos(cutCoord);
          let cut3DPos = convertDwvPosition(cutWorldPos, dwvOrigin);
          cut3DPos.setY(isMaxilla ? maxCutY : mandCutY);
          // drawPoint(cut3DPos, isMaxilla ? 0x0000ff : 0xff0000);

          let matrix4 = new THREE.Matrix4();
          let pivotPoint = new THREE.Vector3();
          if (rotationExist) {
            let rotvec = [
              +rot_vectors[i][2],
              +rot_vectors[i][0],
              +rot_vectors[i][1],
            ];
            //rot_vectors[i];
            let lb = isMaxilla ? "max" : "mand";
            const angle = Math.hypot(
              rot_vectors[i][0],
              rot_vectors[i][1],
              rot_vectors[i][2],
            );
            let euler = rotvecToEuler(rotvec);
            // console.log(`${lb}${i}:`, rotvec, angle, euler);

            let xRad = THREE.MathUtils.degToRad(+rot_vectors[i][2]); //+rot_vectors[i][1]);
            let yRad = THREE.MathUtils.degToRad(+rot_vectors[i][0]); //+rot_vectors[i][2]);
            let zRad = THREE.MathUtils.degToRad(+rot_vectors[i][1]); //+rot_vectors[i][0]);
            // euler = new THREE.Euler(xRad, yRad, zRad, "ZYX");
            // matrix4 = new THREE.Matrix4().makeRotationFromEuler(euler);
            const rot_center = new THREE.Vector3(
              rot_centers_x[i],
              rot_centers_y[i],
              rot_centers_z[i],
            );
            const rot_center2 = new THREE.Vector3(
              rot_centers_x[i] + rot_vectors[i][0],
              rot_centers_y[i] + rot_vectors[i][1],
              rot_centers_z[i] + rot_vectors[i][2],
            );
            const rotCenterWorldPos =
              dwvController.convertPlanePosToWorldPos(rot_center);
            const rotCenterWorldPos2 =
              dwvController.convertPlanePosToWorldPos(rot_center2);
            pivotPoint = convertDwvPosition(rotCenterWorldPos, dwvOrigin);
            const pivotPoint2 = convertDwvPosition(
              rotCenterWorldPos2,
              dwvOrigin,
            );
            // if (isMaxilla) {
            //   drawPoint(pivotPoint, isMaxilla ? 0x0000ff : 0xff0000);
            //   drawLine(pivotPoint2, pivotPoint);
            // }
          }

          const derivCoord = new THREE.Vector3(
            derivArr[i][0],
            derivArr[i][1],
            0,
          );
          derivCoord.normalize();
          const axisOfRotation = new THREE.Vector3(0, 0, 1);
          derivCoord.applyAxisAngle(axisOfRotation, Math.PI * 0.5);
          let dirCoord = new THREE.Vector3().addVectors(
            cutCoord,
            derivCoord.clone().multiplyScalar(extensionLength * 2),
          );
          const dirWorldPos = dwvController.convertPlanePosToWorldPos(dirCoord);
          const dir3DPos = convertDwvPosition(dirWorldPos, dwvOrigin);
          dir3DPos.setY(cut3DPos.y);
          if (i > 0) {
            const obj0 = retCutObjects[i - 1];
            cut3dLength += cut3DPos.distanceTo(obj0.cutPoint);
          }
          const cutObj = createCutObjFromPoss(
            cut3dLength,
            cut3DPos,
            dir3DPos,
            isMaxilla,
            matrix4,
            pivotPoint,
          );
          if (rotationExist) {
            cutObj.angleX = +rot_vectors[i][1];
            cutObj.angleY = +rot_vectors[i][2];
            cutObj.angleZ = +rot_vectors[i][0];
          }

          retCutObjects.push(cutObj);
          // drawPlane(cutObj.cutPlane, isMaxilla ? 0x0000ff : 0xff0000);
        }

        cutContext.cutLength = Math.floor(cut3dLength);
        cutContext.cutMax = cutContext.cutEnd = cutContext.cutLength - 1;
        let mid = Math.round(cutContext.cutMax / 2);
        cutContext.cutStart = mid - 5; //+Math.round(cutContext.cutMax * 0.27);
        cutContext.cutEnd = mid + 6; //+Math.round(cutContext.cutMax * 0.41);
        cutContext.cutStart = 134;
        cutContext.cutEnd = 134;
        console.log("getCutObjectsFromCoordJson 2", cut3dLength, cutContext);
      }
    }
  } catch (error) {
    console.log("ERROR getCutObjectsFromCoordJson", isMaxilla, error);
  }
  return retCutObjects;
}

/**
 * @param {number[]} rotvec - [rx, ry, rz], magnitude = angle in radians
 * @param {string} order - Three.js Euler order, e.g. 'XYZ' (intrinsic)
 * @returns {THREE.Euler} angles in RADIANS
 */
function rotvecToEuler(rotvec, order = "XYZ") {
  const [rx, ry, rz] = rotvec;
  const angle = Math.hypot(rx, ry, rz); // total rotation magnitude
  const q = new THREE.Quaternion();

  if (angle < 1e-12) {
    q.identity();
  } else {
    const s = Math.sin(angle / 2) / angle; // axis * sin(angle/2)
    q.set(rx * s, ry * s, rz * s, Math.cos(angle / 2));
  }

  return new THREE.Euler().setFromQuaternion(q, order);
}

async function showDicomCutReportDlg(maxillaUrl, mandibleUrl) {
  cutContext.savedCutReports = vueApp.savedReports;
  await loadSettings();

  await initializeControls();

  globalKonvaStage = new Konva.Stage({
    container: "cutReportEditKonva", // id of container <div>
    width: 300,
    height: 600,
    opacity: 0.5,
  });
  globalKonvaStage.isPopup = true;
  // console.log("konvaStage 1", konvaStage);
  globalKonvaLayer = new Konva.Layer();
  globalKonvaStage.add(globalKonvaLayer);

  dentations[0].imageUrl = maxillaUrl;
  dentations[1].imageUrl = mandibleUrl;

  document.getElementById("cutReportEditPopup").style.display = "none";
  let img = document.getElementById("cutReportImg");
  if (!img.src) {
    img.src = cutContext.dentation.imageUrl;
    img.onload = function () {
      // console.log("cutReportImg onload ", img.width, img.height);
      cutContext.panoramicWidth = img.width;
      cutContext.panoramicHeight = img.height;
      hideLoader();
      $("#dicomCutReportDlg").modal();
    };
    img.onerror = function () {
      console.log("cutReportImg onerror ", img);
      img.src = null;
      hideLoader();
    };
  } else {
    hideLoader();
    $("#dicomCutReportDlg").modal();
  }
}

async function initializeControls() {
  if (!controlsInitialized) {
    let ctrls = [DicomCutReportDlg];
    ctrls.forEach((component) => {
      const componentInstance = new Vue({
        ...component,
        propsData: {
          context: cutContext,
        },
      });
      const vueContainer = document.createElement("div");
      document.getElementById("main_div").appendChild(vueContainer);
      componentInstance.$mount(vueContainer);
    });

    await delay(100);
    // initCutImageMenu();
  }
}

function stopEvent(event, stopPropagation) {
  if (event) {
    if (event.preventDefault) {
      //  if (event.preventDefault != undefined) {
      event.preventDefault();
    }
    if (stopPropagation && event.stopPropagation) {
      event.stopPropagation();
    }
  }
}

//#endregion

//#region Settings

async function loadSettings() {
  const settingsUrl = `${vueApp.stlBaseUrl}/cutReportSettings.json`;
  try {
    let response = await fetch(settingsUrl, {
      method: "GET",
      cache: "no-cache",
    });
    if (response.ok) {
      const savedContext = await response.json();
      cutContext.cutStart = savedContext.cutStart;
      cutContext.cutEnd = savedContext.cutEnd;
      cutContext.sliceInterval = savedContext.sliceInterval;
      cutContext.imagesPerLine = savedContext.imagesPerLine;
      cutContext.showLines = savedContext.showLines;
      cutContext.panoOnEachPage = savedContext.panoOnEachPage;
      cutContext.pano3dSideBySide = savedContext.pano3dSideBySide;
      cutContext.sharpImageValue = savedContext.sharpImageValue;
      if (savedContext.nerveColor)
        cutContext.nerveColor = savedContext.nerveColor;
      if (savedContext.measureColor)
        cutContext.measureColor = savedContext.measureColor;
      if (savedContext.widthColor)
        cutContext.widthColor = savedContext.widthColor;
      // cutContext.backgroundColor = savedContext.backgroundColor;

      cutContext.savedCutImages = savedContext.savedCutImages;
      cutContext.lineFrequency =
        lineFrequencies[savedContext.lineFrequencyIndex];
      cutContext.dentation = dentations[savedContext.dentationIndex];
      cutContext.nerveLineLogic =
        nerveLineLogics[savedContext.nerveLineLogicIndex];
    }
  } catch (e) {
    console.log(`cutReportSettings error: ${e}`);
  }
}

async function saveSettings() {
  showLoader(`Saving settings`, `Please wait...`, true);
  let savedContext = {
    cutStart: cutContext.cutStart,
    cutEnd: cutContext.cutEnd,
    cutMin: cutContext.cutMin,
    cutMax: cutContext.cutMax,
    cutLength: cutContext.cutLength,
    cutStep: cutContext.cutStep,
    sliceInterval: cutContext.sliceInterval,
    lineFrequencyIndex: lineFrequencies.indexOf(cutContext.lineFrequency),
    dentationIndex: dentations.indexOf(cutContext.dentation),
    nerveLineLogicIndex: nerveLineLogics.indexOf(cutContext.nerveLineLogic),
    imagesPerLine: cutContext.imagesPerLine,
    showLines: cutContext.showLines,
    panoOnEachPage: cutContext.panoOnEachPage,
    pano3dSideBySide: cutContext.pano3dSideBySide,
    sharpImageValue: cutContext.sharpImageValue,
    nerveColor: cutContext.nerveColor,
    measureColor: cutContext.measureColor,
    backgroundColor: cutContext.backgroundColor,
    savedCutImages: [],
  };
  for (const cutImage of cutContext.cutImages) {
    savedContext.savedCutImages.push({
      cutShift: cutImage.cutShift,
      cutPoint: cutImage.cutPoint,
      dirPoint: cutImage.dirPoint,
      isUpper: cutImage.isUpper,
      angle: cutImage.angle,
      opPoint: cutImage.opPoint,
      unitDir: cutImage.unitDir,
      midPoint: cutImage.midPoint,
      nervePoint: cutImage.nervePoint,
      showLines: cutImage.showLines,
      topMidPoint: cutImage.topMidPoint,
      topDistance: cutImage.topDistance,
      nerveDistance: cutImage.nerveDistance,
      topPoint1: cutImage.topPoint1,
      topPoint2: cutImage.topPoint2,
    });
  }

  await saveCutReportSettings(savedContext);

  hideLoader();
}

//#endregion

//#region Pdf Report

function showReportOptions() {
  cutContext.reportData.reportName = `cutsReport_${getFormattedDate(true)}.pdf`;
  document.getElementById("cutReportPdfOptions").style.display = "block";
}

async function saveCutsReport(pdfFunctionI) {
  console.log("saveCutsReport", pdfFunctionI);

  showLoader(`Preparing data`, `Please wait...`, true);
  cutContext.showTopControls = true;
  await delay(100);
  if (!lastGeneratedPdf || cutContext.pdfChanged) {
    lastGeneratedPdf = await generateCutsReportPdf(cutContext.reportData);
    // cutContext.pdfChanged = false;
  }

  if (pdfFunctionI === 1) {
    savePdfLocal(lastGeneratedPdf);
  } else if (pdfFunctionI === 2) {
    let result = await savePdfInDb(lastGeneratedPdf);
  }

  hideLoader();
}

async function generateCutsReportPdf(dataToInclude) {
  // console.log("generateCutsReportPdf");
  for (const app of ReportApps) {
    const layerGroup = app.getLayerGroupByDivId(app.divId);
    const viewLayer = layerGroup.getActiveViewLayer();
    viewLayer.resetRotationMatrix();
    app.resetZoom();
  }

  // const patientData =
  await getPatientData();

  const pdf = new jsPDF();
  const pdfWidth = pdf.internal.pageSize.getWidth(); // 210
  const pdfHeight = pdf.internal.pageSize.getHeight(); // 297
  pdf.setFillColor(cutContext.backgroundColor);
  pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
  let isWhite = isWhiteColor(cutContext.backgroundColor);
  let foregroundColor = isWhite ? "#000000" : "#FFFFFF";
  pdf.setTextColor(foregroundColor);

  const headerHeight = 30;
  const marginTop = 2;
  const marginLeft = 2;
  createPdfHeader(pdf, dataToInclude, headerHeight, marginTop, marginLeft);
  pdf.setTextColor(foregroundColor);

  const fontSize = 14;
  const imgLbX = 1;
  const imgLbY = 4;
  const imgX = 2;
  let imgFontSize = fontSize - 5;

  pdf.setFontSize(fontSize + 6);
  pdf.setFont("helvetica");
  const lineHeightFactor = 0.7;

  let now = new Date();

  let text = `Cuts Report`;
  //  ${now.getFullYear()}/${
  //   now.getMonth() + 1
  // }/${now.getDate()}`;
  let textDims = pdf.getTextDimensions(text);
  let headerLeft = pdfWidth / 2 - textDims.w / 2;
  let headerTop = headerHeight + 12; //pdfHeight / 2 - textDims.h;
  pdf.text(text, headerLeft, headerTop);
  pdf.setFontSize(fontSize);
  let textTop = headerTop + textDims.h;

  // let patientName = `${vueApp.patientFirstName} ${vueApp.patientLastName}`;
  // textDims = pdf.getTextDimensions(patientName);
  // let textHeight = textDims.h;
  // let textWidth = textDims.w;
  // let lineHeight = textHeight * 2 * lineHeightFactor;
  // let textLeft = pdfWidth / 2 - textWidth / 2;
  // pdf.text(patientName, textLeft, textTop);
  // textTop += lineHeight;

  let imgGap = 1;
  let imgCount = cutContext.imagesPerLine;
  let imgWidth = (pdfWidth - imgX * 2 - imgGap * (imgCount - 1)) / imgCount;
  let imgHeight = imgWidth * 1.93;
  let imgLeft = imgX;
  let imgTop = imgX;
  let isFirstPage = true;
  document.querySelectorAll(".cut-tool").forEach((element) => {
    element.style.display = "none";
  });
  let images = [];
  let imageDivs = document.getElementsByClassName("cut-image-div");
  for (const div of imageDivs) {
    let imgCanvas = await html2canvas(div, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      allowTaint: true,
      logging: false,
    });
    let img = imgCanvas.toDataURL("image/jpeg");
    images.push(img);
  }

  let shifts = getCutShifts();
  let middleCut = Math.round((cutContext.cutMax - cutContext.cutMin) / 2);
  console.log("middleCut", middleCut);

  let element = document.getElementById("cutReportImgCtrl");
  let canvas = await html2canvas(element, {
    scale: 2, // Higher scale for better quality
    useCORS: true,
    allowTaint: true,
    logging: false,
  });
  console.log("html2canvas", canvas);
  let imgPanoramic = canvas.toDataURL("image/jpeg");
  let imgFullWidth = pdfWidth - imgX * 2;
  let imgPanHeight =
    imgFullWidth * (cutContext.panoramicHeight / cutContext.panoramicWidth);

  let image3dObj =
    shifts[0] < middleCut ? await get3DImageObj(-45) : await get3DImageObj(45);

  let img3d = image3dObj.data;
  let img3dWidth = imgFullWidth;
  let img3dHeight = img3dWidth * (image3dObj.height / image3dObj.width);

  let image3dRight = img3d;
  if (shifts[0] < middleCut) {
    await delay(100);
    image3dObj = await get3DImageObj(45);
    image3dRight = image3dObj.data;
  }

  // pdf.addImage(img3d, "JPEG", imgX, textTop, img3dWidth, img3dHeight);

  let leftPanoramic = null;
  let rightPanoramic = null;
  if (cutContext.pano3dSideBySide) {
    imgFullWidth = imgFullWidth / 2;
    img3dHeight = imgPanHeight;
    img3dWidth = img3dHeight * (image3dObj.width / image3dObj.height);

    let leftCanvas = cropCanvasHalf(canvas, "left");
    leftPanoramic = leftCanvas.toDataURL("image/jpeg");
    let rightCanvas = cropCanvasHalf(canvas, "right");
    rightPanoramic = rightCanvas.toDataURL("image/jpeg");
    imgPanoramic = shifts[0] < middleCut ? leftPanoramic : rightPanoramic;
  } else {
    pdf.addImage(
      imgPanoramic,
      "JPEG",
      imgX,
      textTop,
      imgFullWidth,
      imgPanHeight,
    );

    textTop += imgPanHeight + 4;

    pdf.addImage(img3d, "JPEG", imgX, textTop, img3dWidth, img3dHeight);

    isFirstPage = false;
    pdf.addPage();
    pdf.setFillColor(cutContext.backgroundColor);
    pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
    imgTop = imgX;
  }

  //#region Other Staff

  // pdf.setTextColor(255, 255, 0);
  pdf.setFontSize(imgFontSize);
  // let bdims = pdf.getTextDimensions(`B`);

  let j = 1;

  if (cutContext.panoOnEachPage || isFirstPage) {
    if (cutContext.pano3dSideBySide) {
      pdf.addImage(
        imgPanoramic,
        "JPEG",
        imgX,
        textTop,
        imgFullWidth,
        imgPanHeight,
      );
      pdf.addImage(
        img3d,
        "JPEG",
        imgX + imgFullWidth + 2,
        textTop,
        img3dWidth,
        img3dHeight,
      );
      imgTop = textTop + imgPanHeight + 4;
    } else {
      pdf.addImage(
        imgPanoramic,
        "JPEG",
        imgX,
        imgTop,
        imgFullWidth,
        imgPanHeight,
      );
      imgTop += imgPanHeight + 4;
    }
  }

  for (const i of shifts) {
    let img = images[j - 1];
    pdf.addImage(img, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);

    if (false) {
      let cutObject = getCutObject(i, cutContext.dentation.isUpper);
      setReportPoints(cutObject);
      // drawPlane(cutObject.cutPlane);
      pdf.setFontSize(imgFontSize);
      pdf.setTextColor(255, 255, 0);
      if (!cutObject.overlayImage) {
        cutObject.overlayImage = getCutImageData(cutObject);
      }
      let showLines = cutContext.showLines && cutObject.showLines;
      let imgData = showLines ? cutObject.overlayImage : cutObject.baseImage;
      if (imgData)
        pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
      pdf.text(`L`, imgLeft + imgLbX, imgTop + imgLbY);
      textDims = pdf.getTextDimensions(`${i}`);
      pdf.text(
        `${i}`,
        imgLeft + imgWidth / 2 - textDims.w / 2,
        imgTop + imgLbY,
      );
      pdf.text(`B`, imgLeft + imgWidth - bdims.w - imgLbX, imgTop + imgLbY);

      pdf.setFontSize(imgFontSize * 0.85);
      if (cutObject.topDistanceText && showLines) {
        pdf.setTextColor(0, 255, 255);
        textDims = pdf.getTextDimensions(cutObject.topDistanceText);
        pdf.text(
          cutObject.topDistanceText,
          imgLeft + imgLbX, //+ imgWidth - textDims.w - imgLbX,
          cutObject.isUpper
            ? imgTop + imgHeight * 0.7
            : imgTop + imgHeight * 0.27,
        );
      }

      if (cutObject.nerveDistanceText && showLines) {
        if (cutObject.isUpper) {
          pdf.setTextColor(255, 255, 0);
        } else {
          pdf.setTextColor(255, 0, 0);
        }
        textDims = pdf.getTextDimensions(cutObject.nerveDistanceText);
        pdf.text(
          cutObject.nerveDistanceText,
          imgLeft + imgLbX, //+ imgWidth - textDims.w - imgLbX,
          cutObject.isUpper
            ? imgTop + imgHeight * 0.27
            : imgTop + imgHeight * 0.7,
        );
      }
    }

    imgLeft += imgWidth + imgGap;

    if (j % imgCount === 0) {
      imgLeft = imgX;
      imgTop += imgHeight + imgGap;
    }

    if (
      imgTop + imgHeight + imgGap > pdfHeight ||
      (cutContext.panoOnEachPage &&
        cutContext.pano3dSideBySide &&
        i + 1 === middleCut)
    ) {
      isFirstPage = false;
      pdf.addPage();
      pdf.setFillColor(cutContext.backgroundColor);
      pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
      imgLeft = imgX;
      imgTop = imgX;
      if (cutContext.panoOnEachPage) {
        imgPanoramic = i + 1 < middleCut ? leftPanoramic : rightPanoramic;
        img3d = i + 1 < middleCut ? img3d : image3dRight;
        if (cutContext.pano3dSideBySide) {
          pdf.addImage(
            imgPanoramic,
            "JPEG",
            imgX,
            imgTop,
            imgFullWidth,
            imgPanHeight,
          );
          pdf.addImage(
            img3d,
            "JPEG",
            imgX + imgFullWidth + 2,
            imgTop,
            img3dWidth,
            img3dHeight,
          );
        } else {
          pdf.addImage(
            imgPanoramic,
            "JPEG",
            imgX,
            imgTop,
            imgFullWidth,
            imgPanHeight,
          );
        }
        imgTop += imgPanHeight + 4;
      }
    }
    j++;
  }

  //#endregion

  document.querySelectorAll(".cut-tool").forEach((element) => {
    element.style.display = "block";
  });

  return pdf;
}

/**
 * Cuts a canvas in half and returns a new canvas.
 * @param {HTMLCanvasElement} sourceCanvas - The original canvas.
 * @param {string} side - 'left', 'right', 'top', or 'bottom'
 * @returns {HTMLCanvasElement} The new cropped canvas.
 */
function cropCanvasHalf(sourceCanvas, side) {
  const newCanvas = document.createElement("canvas");
  const ctx = newCanvas.getContext("2d");

  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  let sx = 0,
    sy = 0,
    sw = w,
    sh = h;

  // Define the source rectangle (the area we are "cutting")
  switch (side) {
    case "left":
      sw = w / 2;
      break;
    case "right":
      sx = w / 2;
      sw = w / 2;
      break;
    case "top":
      sh = h / 2;
      break;
    case "bottom":
      sy = h / 2;
      sh = h / 2;
      break;
    default:
      throw new Error("Side must be 'left', 'right', 'top', or 'bottom'");
  }

  // Set the new canvas dimensions to match the cut piece
  newCanvas.width = sw;
  newCanvas.height = sh;

  // Draw the cropped portion onto the new canvas
  // Parameters: source, sx, sy, sw, sh, dx, dy, dw, dh
  ctx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, sw, sh);

  return newCanvas;
}

async function get3DImageObj(degrees) {
  let imageObj = {};

  if (!degrees) degrees = 45;

  vueApp.saveCameraView();
  vueApp.leaveModes(true);
  let tempColor = viewer.scene.background;
  viewer.scene.background = new THREE.Color(cutContext.backgroundColor);
  let clientRect = viewer.parent_element.getBoundingClientRect();
  let width = (imageObj.width = clientRect.width);
  let height = (imageObj.height = width * 0.68);
  viewer.camera.aspect = width / height;
  viewer.camera.updateProjectionMatrix();
  viewer.renderer.setSize(width, height);

  vueApp.lookAtPoint({ x: 0, y: 0, z: 0 });

  resetCameraUp();

  viewer.camera.position.x = 0;
  viewer.camera.position.y = 0;
  viewer.camera.position.z = defaultZoom * 0.7;
  viewer.camera.position.applyQuaternion(
    new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0), // The positive y-axis
      degreesToRadians(degrees), // The amount of rotation to apply this time
    ),
  );

  viewer.camera.updateProjectionMatrix();
  viewer.controls.update();

  await delay(100);

  imageObj.data = getImgDataForCurrentView();

  setTimeout(function () {
    viewer.scene.background = tempColor;
    viewer.do_resize();
    // vueApp.leaveModes(true);
    vueApp.switchToSavedCameraView();
  }, 100);

  return imageObj;
}

function savePdfLocal(pdf) {
  let fullName = cutContext.reportData.reportName;
  pdf.save(fullName);
}

async function savePdfInDb(pdf) {
  const blob = pdf.output("blob");
  let fullName = cutContext.reportData.reportName;
  let result = await saveCutReportPdfInS3(blob, fullName);
  let report = {
    name: fullName,
    directory: "cbctCutReports",
    url: `${vueApp.stlBaseUrl}/reports/cbctCutReports/${fullName}`,
  };
  cutContext.savedCutReports.push(report);
  vueApp.hasReports = true;
  return result;
}

async function showCutReportsListDlg() {
  console.log("showCutReportsListDlg");
  cutContext.savedCutReports = vueApp.savedReports;
  //[vueApp.savedReports[0]];
  let dlg = document.getElementById("reportsListDlg");
  if (!dlg) {
    var ctrls = [ReportsListDlg];
    ctrls.forEach((component) => {
      const componentInstance = new Vue({
        ...component,
        propsData: { context: cutContext },
      });
      const vueContainer = document.createElement("div");
      document.getElementById("views_cont").appendChild(vueContainer);
      componentInstance.$mount(vueContainer);
    });
  }

  $("#reportsListDlg").modal();
}

async function removePdfReport(report) {
  console.log("removePdfReport");

  swal({
    text: "Are you sure you want to delete this report?",
    buttons: {
      yes: { text: "Yes", value: true },
      no: { text: "No", value: false },
    },
  }).then(async (willDelete) => {
    if (willDelete) {
      showLoader(`Updating data`, `Please wait...`, true);
      let index = cutContext.savedCutReports.indexOf(report);
      if (index >= 0) {
        cutContext.savedCutReports.splice(index, 1);
      }
      index = vueApp.savedReports.indexOf(report);
      if (index >= 0) {
        vueApp.savedReports.splice(index, 1);
      }

      if (!vueApp.savedReports || !vueApp.savedReports.length) {
        vueApp.hasReports = false;
        $("#reportsListDlg").modal("hide");
      }

      await sendRemovePdfReportRequest(report);
      hideLoader();
    }
  });
}

//#endregion

//#region Cut image

//#region Cut Object

function getCutObject(cutShift, isUpper) {
  console.log("getCutObject", cutShift, isUpper);
  let tempObjects = cutObjects.filter((c) => c.isUpper === isUpper);
  let obj1Index = tempObjects.findLastIndex((c) => c.cutShift <= cutShift);
  let obj1 = tempObjects[obj1Index];
  if (obj1) {
    if (obj1.cutShift == cutShift) {
      return obj1;
    }
    let obj2 = tempObjects[obj1Index + 1];
    if (obj2) {
      const shiftDif = obj2.cutShift - obj1.cutShift;
      const dif1 = cutShift - obj1.cutShift;
      //   const dif2 = obj2.cutShift - cutShift;
      const shiftRel = dif1 / shiftDif;
      const cutPoint1 = obj1.cutPoint;
      const cutPoint2 = obj2.cutPoint;
      const cutDifX = cutPoint2.x - cutPoint1.x;
      const cutDifZ = cutPoint2.z - cutPoint1.z;
      let cut3DPos = new THREE.Vector3(
        cutPoint1.x + cutDifX * shiftRel,
        cutPoint1.y,
        cutPoint1.z + cutDifZ * shiftRel,
      );

      const dirPoint1 = obj1.dirPoint;
      const dirPoint2 = obj2.dirPoint;
      const dirDifX = dirPoint2.x - dirPoint1.x;
      const dirDifZ = dirPoint2.z - dirPoint1.z;
      let dir3DPos = new THREE.Vector3(
        dirPoint1.x + dirDifX * shiftRel,
        dirPoint1.y,
        dirPoint1.z + dirDifZ * shiftRel,
      );

      const matrix1 = obj1.rotMatrix;
      const matrix2 = obj2.rotMatrix;
      const rotMatrix = getMiddleMatrix(matrix1, matrix2, shiftRel);

      const pivotPoint1 = obj1.pivotPoint;
      const pivotPoint2 = obj2.pivotPoint;
      const pivotDifX = pivotPoint2.x - pivotPoint1.x;
      const pivotDifZ = pivotPoint2.z - pivotPoint1.z;
      let pivotPoint = new THREE.Vector3(
        pivotPoint1.x + pivotDifX * shiftRel,
        pivotPoint1.y,
        pivotPoint1.z + pivotDifZ * shiftRel,
      );

      const tempObj = createCutObjFromPoss(
        cutShift,
        cut3DPos,
        dir3DPos,
        isUpper,
        null,
        pivotPoint,
      );
      // drawPlane(tempObj.cutPlane, 0xff0000);

      const cutObj = createCutObjFromPoss(
        cutShift,
        cut3DPos,
        dir3DPos,
        isUpper,
        rotMatrix,
        pivotPoint,
      );
      const index2 = cutObjects.indexOf(obj2);
      cutObjects.splice(index2, 0, cutObj);
      // drawPlane(cutObj.cutPlane, 0x0000ff);

      // let angl = -getXAngle(cutObj.dirPoint, cutObj.cutPoint);
      // console.log(angl);

      // drawPlane(cutObj.cutPlane, 0xff0000);
      // drawLine(cutObj.cutPoint, cutObj.dirPoint);
      return cutObj;
    } else {
      return obj1;
    }
  }
}

function getMiddleMatrix(matrixA, matrixB, rel) {
  // 1. Create temporary variables to hold components
  const posA = new THREE.Vector3(),
    qA = new THREE.Quaternion(),
    scaleA = new THREE.Vector3();
  const posB = new THREE.Vector3(),
    qB = new THREE.Quaternion(),
    scaleB = new THREE.Vector3();

  // 2. Decompose both matrices into position, rotation (quaternion), and scale
  matrixA.decompose(posA, qA, scaleA);
  matrixB.decompose(posB, qB, scaleB);

  // 3. Interpolate each component by 0.5 (the middle point)
  const midPos = new THREE.Vector3().lerpVectors(posA, posB, rel);
  const midRot = qA.slerp(qB, rel);
  //new THREE.Quaternion().slerpQuaternions(qA, qB, rel);
  const midScale = new THREE.Vector3().lerpVectors(scaleA, scaleB, rel);

  // 4. Recompose the middle components back into a single Matrix4
  const middleMatrix = new THREE.Matrix4();
  middleMatrix.compose(midPos, midRot, midScale);

  return middleMatrix;
}

function createCutObjFromPoss(
  cutShift,
  cut3DPos,
  dir3DPos,
  isUpper,
  rotMatrix,
  pivotPoint,
) {
  const unitDir = new THREE.Vector3()
    .subVectors(cut3DPos, dir3DPos)
    .normalize();
  const minY = isUpper ? maxillaBox.min.y : mandilaBox.min.y;
  const maxY = pivotPoint.y; //isUpper ? maxillaBox.max.y : mandilaBox.max.y;
  //Math.max(maxillaBox.max.y, cut3DPos.y);
  const opPos = cut3DPos.clone().add(unitDir.multiplyScalar(extensionLength));
  const cutPlane = getVerticalPlane(
    new THREE.Vector3(dir3DPos.x, minY, dir3DPos.z),
    new THREE.Vector3(opPos.x, maxY, opPos.z),
    rotMatrix,
    pivotPoint,
  );

  const cutObj = {
    cutShift: cutShift,
    cutPoint: cut3DPos,
    dirPoint: dir3DPos,
    isUpper: isUpper,
    angle: -getXAngle(dir3DPos, cut3DPos),
    cutPlane: cutPlane,
    opPoint: opPos,
    unitDir: new THREE.Vector3().subVectors(cut3DPos, dir3DPos).normalize(),
    midPoint: null,
    nervePoint: null,
    // wrappers: null,
    showLines: true,
    rotMatrix: rotMatrix,
    pivotPoint: pivotPoint,
  };
  cutObj.angle -= 90;

  return cutObj;
}

//!!!
function setReportPoints(cutObj) {
  if (cutObj.midPoint) {
    return;
  }

  let cut3DPos = cutObj.cutPoint;
  let dir3DPos = cutObj.dirPoint;
  cutObj.midPoint = cut3DPos;

  let teeth = cutObj.isUpper ? vueApp.upperTeeth : vueApp.lowerTeeth;
  let crossMeshes = teeth.map((t) => viewer.get_model_mesh(t.id));

  const wrappers = getNearestTeethWrappers(teeth, cut3DPos, 3);
  let nerve = vueApp.nerves[0];
  let nerveMesh = viewer.get_model_mesh(nerve.id);
  if (nerve && !cutObj.isUpper && nerveMesh && nerveMesh.geometry) {
    wrappers.push({
      model: nerve,
      mesh: nerveMesh,
      distance: 0,
      isNerve: true,
    });
  }

  let bone = vueApp.bones.find((b) => b.isUpper === cutObj.isUpper);
  let boneMesh = bone ? viewer.get_model_mesh(bone.id) : null;
  if (bone) {
    wrappers.push({
      model: bone,
      mesh: boneMesh,
      distance: 0,
      isBone: true,
    });
    crossMeshes.push(boneMesh);
  }

  // drawPlane(cutObj.cutPlane, 0xFF0000);

  let allPoints = [];
  let teethPoints = [];
  for (const wrapper of wrappers) {
    let mesh = wrapper.mesh;
    let limitBox = null;
    if (wrapper.isNerve || wrapper.isBone) {
      let fullBox = cutObj.isUpper ? fullMaxillaBox : fullMandilaBox;
      if (cutObj.isUpper) {
        limitBox = dir3DPos.x < fullBox.center.x ? leftMaxBox : rightMaxBox;
      } else {
        limitBox = dir3DPos.x < fullBox.center.x ? leftMandBox : rightMandBox;
      }

      if (wrapper.isBone) {
        let diff = Math.abs(fullBox.center.x - dir3DPos.x);
        // console.log("fullBox diff", diff);
        if (diff < 3) limitBox = fullBox;
      }
    }

    if (mesh.rootAnchorMesh) {
      mesh = mesh.rootAnchorMesh;
    }

    let crossPoints = getCrossPointsForPlane(
      mesh,
      cutObj.cutPlane,
      limitBox,
      wrapper.isNerve,
    );
    let topPointsObj = null;

    if (wrapper.isNerve) {
      cutObj.nervePoint = getMidYPoint(crossPoints);
      // drawPoint(cutObj.nervePoint);
    } else if (wrapper.isBone) {
      // saveStlFromMesh(mesh, "maxilla.stl");
      // drawPointsArray(crossPoints, viewer.scene, 0xffff00);
      // saveLocalJson(crossPoints, "bonePoints89.json");

      if (teethPoints) {
        // saveLocalJson(teethPoints, "teethPoints.json");
        // drawPointsArray(teethPoints, viewer.scene, 0xff0000);
        // let pcaObj = getPcaObj(teethPoints);
        // drawPcaObj(pcaObj, viewer.scene);
        // let crossGeom = new THREE.BufferGeometry();
        // crossGeom.setFromPoints(teethPoints);
        // crossGeom.computeBoundingBox();
        // let crossMesh = new THREE.Line(crossGeom, lineMaterial2);
        // viewer.scene.add(crossMesh);
        // let geomBox = offsetBox(crossGeom.boundingBox);
        // crossPoints = crossPoints.concat(teethPoints);
      }

      let p1 = [...crossPoints].sort(function (a, b) {
        return cutObj.isUpper ? a.y - b.y : b.y - a.y;
      })[0];
      let tempPoints = crossPoints.filter(
        (p) => Math.abs(p1.y - p.y) < maxApexOffset,
      );
      if (tempPoints.length > 1) {
        crossPoints = tempPoints;
      }
      // let p1 = [...crossPoints].sort(function (a, b) {
      //   return a.y - b.y; // return b.y - a.y;
      // })[0];
      let p2 = getNextOpPoint(crossPoints, p1, 3, 15, cutObj.isUpper);
      // cutObj.isUpper
      //   ? getNextOpPoint(crossPoints, p1, 5, 15)
      //   : getNextHighestPoint(crossPoints, p1, 5, 15);
      topPointsObj = {
        point1: p1,
        point2: p2,
        midPoint: null,
      };
      topPointsObj.midPoint = p2
        ? new THREE.Vector3(
            (p1.x + p2.x) / 2,
            (p1.y + p2.y) / 2,
            (p1.z + p2.z) / 2,
          )
        : p1;

      if (cutObj.isUpper) {
        let normal = cutObj.cutPlane.normal.clone();
        // if (cutObj.isUpper) normal.negate();
        // drawLine(p1, p2, 0x00ff00);

        let perpLine = getPerpendicularLineFromCenter(
          p1,
          p2,
          maxBoneDistance,
          normal,
        );

        if (perpLine.end.y < p1.y && perpLine.end.y < p2.y) {
          normal.negate();
          perpLine = getPerpendicularLineFromCenter(
            p1,
            p2,
            maxBoneDistance,
            normal,
          );
        }
        if (perpLine) {
          drawLine(perpLine.start, perpLine.end);

          let opPoint = getOpCrossPoint(mesh, perpLine.start, perpLine.end);
          if (
            opPoint &&
            opPoint.distanceTo(perpLine.start) <= maxBoneDistance
          ) {
            cutObj.nervePoint = opPoint;
          } else {
            cutObj.nervePoint = perpLine.end;
          }
        }
      }
      // console.log("topPointsObj", topPointsObj, cutObj.nervePoint);

      // if (cutObj.isUpper) {
      //   crossPoints.sort(function (a, b) {
      //     return a.y - b.y; // return b.y - a.y;
      //   });
      // } else {
      //   crossPoints.sort(function (a, b) {
      //     return b.y - a.y;
      //   });
      // }
      // drawPointsArray(crossPoints, viewer.scene);

      if (!topPointsObj)
        topPointsObj = getTopPointsObj(
          crossPoints,
          minBoneJawWidth,
          cutObj.isUpper,
        );

      if (topPointsObj && topPointsObj.point2) {
        allPoints.push(topPointsObj.point1);
        allPoints.push(topPointsObj.point2);

        cutObj.topPoint1 = topPointsObj.point1;
        cutObj.topPoint2 = topPointsObj.point2;

        if (cutObj.nervePoint && cutContext.nerveLineLogic.index != 1) {
          cutObj.topMidPoint = projectPointOnLine(
            cutObj.nervePoint,
            cutObj.topPoint1,
            cutObj.topPoint2,
          );
        } else {
          cutObj.topMidPoint = topPointsObj.midPoint;
        }

        cutObj.topDistance = cutObj.topPoint1.distanceTo(cutObj.topPoint2);
        cutObj.topDistanceText = cutObj.topDistanceTextP =
          cutObj.topDistance.toFixed(2) + " mm";
      }
    } else {
      if (crossPoints) {
        teethPoints.push(...crossPoints);
      }
    }

    if (crossPoints) {
      allPoints.push(...crossPoints);
    }
  }

  // drawPlane(cutObj.cutPlane);
  // drawPointsArray(allPoints, viewer.scene);

  if (cutObj.topMidPoint && cutObj.nervePoint) {
    allPoints.push(cutObj.nervePoint);
    cutObj.nerveDistance = cutObj.topMidPoint.distanceTo(cutObj.nervePoint);
    cutObj.nerveDistanceText = cutObj.nerveDistanceTextP =
      cutObj.nerveDistance.toFixed(2) + " mm";
    // drawPoint(cutObj.nervePoint, 0xff0000);
    // drawLine(cutObj.nervePoint, cutObj.topMidPoint);
  }

  cutObj.midPoint = getMidYPoint(allPoints); // allPoints teethPoints

  if (!cutObj.midPoint) {
    if (cutObj.topMidPoint) {
      cutObj.midPoint = cutObj.topMidPoint.clone();
    } else {
      let teetBox = cutObj.isUpper ? maxillaBox : mandilaBox;
      cutObj.midPoint = getMidYPoint(allPoints);
      if (cutObj.midPoint) cutObj.midPoint.setY(teetBox.center.y);
    }
  } else if (cutObj.topMidPoint) {
    cutObj.midPoint = new THREE.Vector3(
      (cutObj.midPoint.x + cutObj.topMidPoint.x) / 2,
      (cutObj.midPoint.y + cutObj.topMidPoint.y) / 2,
      (cutObj.midPoint.z + cutObj.topMidPoint.z) / 2,
    );
  }

  if (cutObj.midPoint && cutObj.topMidPoint && cutObj.nervePoint) {
    let imgHeight = cutContext.imgHeight;
    // drawLine(cutObj.topMidPoint, cutObj.nervePoint, 0xff0000);

    const minY = cutObj.midPoint.y - imgHeight / 2;
    const maxY = cutObj.midPoint.y + imgHeight / 2;

    let tempPoint = cutObj.midPoint.clone();
    let unitDir = cutObj.unitDir.clone().negate();
    const min = tempPoint.add(unitDir.multiplyScalar(imgHeight / 4));

    tempPoint = cutObj.midPoint.clone();
    unitDir = cutObj.unitDir.clone();
    const max = tempPoint.add(unitDir.multiplyScalar(imgHeight / 4));
    // drawLine(min, max, 0x0000ff);

    let rectPoints = [
      new THREE.Vector3(min.x, minY, min.z),
      new THREE.Vector3(min.x, maxY, min.z),
      new THREE.Vector3(max.x, maxY, max.z),
      new THREE.Vector3(max.x, minY, max.z),
    ];

    const result = checkLineInRectangle(
      cutObj.topMidPoint,
      cutObj.nervePoint,
      rectPoints,
    );

    if (result.isInside) {
      console.log("Line is fully inside the rectangle");
    } else if (result.crossPoint) {
      console.log("Cross point:", result.crossPoint);
      cutObj.nervePoint = result.crossPoint;
      cutObj.nerveDistance = cutObj.topMidPoint.distanceTo(cutObj.nervePoint);
      cutObj.nerveDistanceText = cutObj.nerveDistanceTextP =
        cutObj.nerveDistance.toFixed(2) + " mm";
    } else {
      console.log("No intersection");
    }
  }

  // drawPoint(cutObj.midPoint, 0x00ff00);
}

function projectPointOnLine(point, linePoint1, linePoint2) {
  // Create vectors
  const p = new THREE.Vector3().copy(point);
  const a = new THREE.Vector3().copy(linePoint1);
  const b = new THREE.Vector3().copy(linePoint2);

  // Calculate direction vector of the line
  const direction = new THREE.Vector3().subVectors(b, a);

  // Vector from line start to point
  const toPoint = new THREE.Vector3().subVectors(p, a);

  // Project toPoint onto direction vector
  const t = toPoint.dot(direction) / direction.dot(direction);

  // Clamp t to [0, 1] for line segment (remove for infinite line)
  const clampedT = Math.max(0, Math.min(1, t));

  // Calculate projection point
  const projection = new THREE.Vector3().copy(a);
  projection.addScaledVector(direction, clampedT);

  return projection;
}

const _projectedPoint = new THREE.Vector2();
function projectPointOnLine2D(ax, ay, bx, by, cx, cy) {
  const dx = bx - ax;
  const dy = by - ay;
  const px = cx - ax;
  const py = cy - ay;

  const lenSq = dx * dx + dy * dy;

  // Edge case: A and B are the same point
  if (lenSq === 0) {
    outVec.set(ax, ay);
    return outVec;
  }

  // Calculate 't'
  let t = (px * dx + py * dy) / lenSq;

  // Clamp 't' (Ternary is slightly faster than Math.max/min in JS)
  // if (clampToSegment) {
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  // }

  // Mutate the pre-allocated vector directly
  _projectedPoint.x = ax + dx * t;
  _projectedPoint.y = ay + dy * t;
  return _projectedPoint;

  // let retval = {
  //   x: ax + dx * t,
  //   y: ay + dy * t,
  // };
  // return retval;
}

// Pre-allocate vectors outside your loop/event listener
const _lineDir = new THREE.Vector2();
const _pointVec = new THREE.Vector2();
const _result = new THREE.Vector2();
function getProjectedPoint2D(
  pointA,
  pointB,
  targetPoint,
  clampToSegment = true,
) {
  _lineDir.subVectors(pointB, pointA);
  _pointVec.subVectors(targetPoint, pointA);

  let t = _pointVec.dot(_lineDir) / _lineDir.lengthSq();

  if (clampToSegment) {
    t = Math.max(0, Math.min(1, t));
  }

  return _result.copy(_lineDir).multiplyScalar(t).add(pointA);
}

function getNearestTeethWrappers(teeth, point, count) {
  let wrappers = [];
  for (const tooth of teeth) {
    const mesh = viewer.get_model_mesh(tooth.id);
    const distance = point.distanceTo(mesh.position);
    const wrapper = {
      model: tooth,
      mesh: mesh,
      distance: distance,
      isNerve: false,
      isBone: false,
    };
    wrappers.push(wrapper);
  }

  if (count) {
    wrappers.sort(function (a, b) {
      return a.distance - b.distance;
    });
    wrappers = wrappers.slice(0, count);
  }
  return wrappers;
}

//#endregion

async function fillCutImages(force) {
  showLoader(`Preparing data`, `Please wait...`, true);
  await switchBonesType(DEFAULT_BONES_TYPE);

  await delay(100);

  cutContext.cutImages.splice(0, cutContext.cutImages.length);

  for (const plane of tempPlanes) {
    viewer.scene.remove(plane);
  }
  tempPlanes = [];

  let shifts = getCutShifts();
  let j = 1;
  for (const i of shifts) {
    let cutObject = getCutObject(i, cutContext.dentation.isUpper);
    cutObject.showLines = j % cutContext.lineFrequency.value == 0;
    setReportPoints(cutObject);
    if (!cutObject.overlayImage || force) {
      cutObject.overlayImage = getCutImageData(cutObject);
    }
    cutContext.cutImages.push(cutObject);
    j++;
  }

  hideLoader();

  if (isFirstGenerate) {
    // cutContext.showTopControls = false;
    isFirstGenerate = false;
  }
}

function getCutShifts() {
  let shifts = [];
  let cutStart = +cutContext.cutStart;
  let cutEnd = +cutContext.cutEnd;
  let sliceInterval = +cutContext.sliceInterval;
  if (cutStart <= cutEnd) {
    for (let i = cutStart; i <= cutEnd; i += sliceInterval) {
      shifts.push(i);
    }
  } else {
    for (let i = cutStart; i >= cutEnd; i -= sliceInterval) {
      shifts.push(i);
    }
  }
  return shifts;
}

function getCutImageData(cutImage) {
  const app = ReportApps[1];
  const app0 = ReportApps[0];
  // let teethBox = cutImage.isUpper ? maxillaBox : mandilaBox;
  let imgHeight = cutContext.imgHeight; //teethBox.size.y + 5;
  let vPosition = (cutImage.center = cutImage.midPoint
    ? cutImage.midPoint
    : cutImage.cutPoint);
  let min = (cutImage.min = new THREE.Vector3(
    vPosition.x,
    vPosition.y - imgHeight / 2,
    vPosition.z + imgHeight / 4,
  ));
  let max = (cutImage.max = new THREE.Vector3(
    vPosition.x,
    vPosition.y + imgHeight / 2,
    vPosition.z - imgHeight / 4,
  ));

  let lineArr = [];
  let pointArr = null;

  let angle = cutImage.angle ? cutImage.angle : 0;
  const dwvPos = getDwvPos(vPosition, app);
  let indexPos = app0.geometry.worldToIndex(dwvPos);
  let size = app0.geometry.getSize();
  let originX = indexPos.get(0) / size.get(0);
  let originY = indexPos.get(1) / size.get(1);
  let originZ = indexPos.get(2) / size.get(2);
  let origin = [originX, originY, originZ];
  const layerGroup = app.getLayerGroupByDivId(app.divId);
  const viewLayer = layerGroup.getActiveViewLayer();
  const viewController = viewLayer.getViewController();
  viewLayer.setRotationAngles(0, 0, angle, app.index, origin);

  let matrix4 = viewLayer.matrix4;
  app.pointMatrix = getMatrixAroundPoint(matrix4, vPosition);
  app.inversePointMatrix = app.pointMatrix.clone().invert();
  let tempPoint = vPosition.clone().applyMatrix4(app.inversePointMatrix);

  const minDwv = getDwvPos(min, app);
  const maxDwv = getDwvPos(max, app);

  if (cutImage.nervePoint) {
    // console.log("obj with nerve", cutObj);
    tempPoint = cutImage.nervePoint
      .clone()
      .applyMatrix4(app.inversePointMatrix);
    const nervePos = getDwvPos(tempPoint, app);
    const nervePoint = new dwv.Point([
      nervePos.get(0),
      nervePos.get(1),
      nervePos.get(2),
      0,
    ]);

    pointArr = [
      {
        pos: nervePoint,
        color: cutImage.isUpper ? "yellow" : "red",
        // radius: 6,
      },
    ];
    if (cutImage.topMidPoint) {
      tempPoint = cutImage.topMidPoint
        .clone()
        .applyMatrix4(app.inversePointMatrix);
      const topMidPos = getDwvPos(tempPoint, app);
      lineArr.push({
        start: nervePoint,
        end: topMidPos,
        color: cutImage.isUpper ? "yellow" : "red",
        dashed: false,
        isNerve: true,
      });
    }
  }

  if (cutImage.topPoint1 && cutImage.topPoint2) {
    tempPoint = cutImage.topPoint1.clone().applyMatrix4(app.inversePointMatrix);
    const topPos1 = getDwvPos(tempPoint, app);
    tempPoint = cutImage.topPoint2.clone().applyMatrix4(app.inversePointMatrix);
    const topPos2 = getDwvPos(tempPoint, app);
    lineArr.push({
      start: topPos1,
      end: topPos2,
      color: "aqua",
      dashed: false,
      isNerve: false,
    });
  }

  const imagesObj = getImagesObjFromDwvPosition(
    app,
    dwvPos,
    minDwv,
    maxDwv,
    lineArr,
    pointArr,
    [],
    cutContext.sharpImageValue,
  );

  cutImage.baseImage = imagesObj.baseImage;
  cutImage.overlayImage = imagesObj.overlayImage;
  if (!cutImage.imagesObj) cutImage.imagesObj = imagesObj;

  // for (const line of imagesObj.lines) {
  //   if (!line.isNerve) {
  //     let start0 = {
  //       x: line.start.x / 2,
  //       y: line.start.y / 2,
  //     };
  //     let startPoint = getPoint3dFromImagePoint(line.start, imagesObj);
  //     let startPoint0 = getPoint3dFromImagePoint(start0, imagesObj);
  //     drawLine(startPoint, startPoint0, 0x0000ff);
  //     drawPoint(startPoint, 0xff0000);
  //     drawPoint(startPoint0, 0x00ffff);
  //   }
  // }
  // drawPlane(cutImage.cutPlane);
  return cutImage.overlayImage;
}

function getPoint3dFromImagePoint(point, imagesObj) {
  let dwvPos = getDwvPosFromImagePoint(point, imagesObj);
  let point3d = convertDwvPosition(dwvPos, imagesObj.dwvOrigin);
  point3d.applyMatrix4(imagesObj.pointMatrix);
  return point3d;
}

function getDwvPosFromImagePoint(point, imagesObj) {
  const app = ReportApps[1];
  const viewLayer = app.getStage().getActiveLayerGroup().getActiveViewLayer();
  const viewController = viewLayer.getViewController();

  let cpoint = { x: point.x * imagesObj.width, y: point.y * imagesObj.height };
  const yFlip = imagesObj.yFlip;
  const xShift = imagesObj.xShift;
  const yShift = imagesObj.yShift;
  const imageZoom = imagesObj.imageZoom;
  const planePos = {
    x: (cpoint.x - xShift) / imageZoom,
    y: (cpoint.y - yShift) / yFlip / imageZoom,
  };

  const worldPos = viewController.convertPlanePosToWorldPos(planePos);
  const dwvPosition = new dwv.Point([
    imagesObj.center0, //worldPos.get(0),
    worldPos.get(1),
    worldPos.get(2),
    0,
  ]);

  return dwvPosition;
}
//#endregion

//#region LINES

function showEditPopup(cutImage) {
  if (globalKonvaLayer) {
    globalKonvaLayer.destroyChildren();
  }

  document.getElementById("cutReportEditPopup").style.display = "block";
  drawLinesOnKonva(cutImage, globalKonvaStage, globalKonvaLayer);
  // konvaLayer.batchDraw();
  // konvaLayer.draw();
}

function drawLinesOnKonva(cutImage, konvaStage, konvaLayer, force) {
  // console.log("drawLinesOnKonva", cutImage);
  if (!force) {
    force =
      konvaStage.isPopup || !konvaLayer.children || !konvaLayer.children.length;
  }

  if (force) {
    konvaLayer.destroyChildren();
  }

  let lines = konvaStage.isPopup
    ? cutImage.imagesObj.popupLines
    : cutImage.imagesObj.lines;
  let nervLine = lines.find((x) => x.isNerve);
  for (const imageLine of lines.filter((x) => !x.isNerve)) {
    let childLine = imageLine.isNerve ? null : nervLine;
    imageLine.konvaLine = drawKonvaLine(
      cutImage,
      imageLine,
      childLine,
      konvaStage,
      konvaLayer,
      force,
    );
  }

  if (nervLine)
    nervLine.konvaLine = drawKonvaLine(
      cutImage,
      nervLine,
      null,
      konvaStage,
      konvaLayer,
      force,
    );
}

function drawKonvaLine(
  cutImage,
  imageLine,
  childLine,
  konvaStage,
  konvaLayer,
  force,
) {
  // if (!layer) layer = cutImage.konvaLayer;
  // if (!konvaStage) konvaStage = cutImage.konvaStage;
  const stageWidth = konvaStage.width();
  const stageHeight = konvaStage.height();
  const imagesObj = cutImage.imagesObj;

  let start = imageLine.start;
  let end = imageLine.end;
  if (konvaStage.isPopup) console.log("imageLine", start, end);
  let p1 = { x: stageWidth * start.x, y: stageHeight * start.y };
  let p2 = { x: stageWidth * end.x, y: stageHeight * end.y };
  let color = cutContext.widthColor;
  if (imageLine.isNerve) {
    color = cutImage.isUpper ? cutContext.measureColor : cutContext.nerveColor;
  }
  let anchorRadius = stageWidth / 30;

  let konvaLine = imageLine.konvaLine;
  if (!konvaLine || force) {
    konvaLine = new Konva.Line({
      points: [p1.x, p1.y, p2.x, p2.y],
      stroke: color,
      strokeWidth: 2,
      lineCap: "round",
      lineJoin: "round",
      hitStrokeWidth: 10,
    });
    konvaLine.cutImage = cutImage;
    konvaLine.imageLine = imageLine;
    imageLine.konvaLine = konvaLine;
    konvaLayer.add(konvaLine);
    // Create anchor circles at endpoints
    const anchor1 = (konvaLine.anchor1 = new Konva.Circle({
      x: p1.x,
      y: p1.y,
      radius: anchorRadius,
      fill: color,
      stroke: "#000",
      strokeWidth: 1,
      draggable: true,
    }));
    const anchor2 = (konvaLine.anchor2 = new Konva.Circle({
      x: p2.x,
      y: p2.y,
      radius: anchorRadius,
      fill: color,
      stroke: "#000",
      strokeWidth: 1,
      draggable: true,
      // !imageLine.isNerve || cutContext.nerveLineLogic.index == 2,
      visible: true,
      //!imageLine.isNerve && cutContext.nerveLineLogic.index < 2,
    }));
    konvaLine.draggable(true); //       imageLine.isNerve && cutContext.nerveLineLogic.index == 2,

    konvaLine.on("dragmove", updateAnchorPoints);
    konvaLine.on("dragend", () => {
      updateKonvaLine();
      updateDistances();
    });
    konvaLine.on("mouseenter", function () {
      this.getStage().container().style.cursor = "pointer";
    });

    konvaLine.on("mouseleave", function () {
      this.getStage().container().style.cursor = "default";
    });

    const anchors = [anchor1, anchor2];
    for (const anchor of anchors) {
      konvaLayer.add(anchor);
      anchor.on("dragmove", updateKonvaLine);
      anchor.on("dragend", updateDistances);
      anchor.on("mouseenter", function () {
        this.getStage().container().style.cursor = "pointer";
      });

      anchor.on("mouseleave", function () {
        this.getStage().container().style.cursor = "default";
      });
    }

    function updateAnchorPoints() {
      let points = konvaLine.points();
      let x = konvaLine.x();
      let y = konvaLine.y();
      konvaLine.anchor1.x(points[0] + x);
      konvaLine.anchor1.y(points[1] + y);
      konvaLine.anchor2.x(points[2] + x);
      konvaLine.anchor2.y(points[3] + y);
      konvaLayer.batchDraw();
    }

    function updateKonvaLine() {
      // console.log("updateKonvaLine");
      let x1 = anchor1.x();
      let y1 = anchor1.y();
      let x2 = anchor2.x();
      let y2 = anchor2.y();
      const points = [x1, y1, x2, y2];
      konvaLine.x(0);
      konvaLine.y(0);
      konvaLine.points(points);

      if (childLine && cutContext.nerveLineLogic.index < 2) {
        const kcl = childLine.konvaLine; // konvaChildLine

        if (cutContext.nerveLineLogic.index == 0) {
          let point2 = projectPointOnLine2D(
            x1,
            y1,
            x2,
            y2,
            kcl.anchor1.x(),
            kcl.anchor1.y(),
          );
          kcl.anchor2.x(point2.x);
          kcl.anchor2.y(point2.y);
        } else {
          kcl.anchor2.x((x1 + x2) / 2);
          kcl.anchor2.y((y1 + y2) / 2);
        }

        const cpoints = [
          kcl.anchor1.x(),
          kcl.anchor1.y(),
          kcl.anchor2.x(),
          kcl.anchor2.y(),
        ];
        kcl.points(cpoints);
      }

      konvaLayer.batchDraw();
    }

    // !!! not update start and end ?
    function updateDistances() {
      let tempStart = {
        x: anchor1.x() / stageWidth,
        y: anchor1.y() / stageHeight,
      };
      let tempEnd = {
        x: anchor2.x() / stageWidth,
        y: anchor2.y() / stageHeight,
      };
      let start3d = getPoint3dFromImagePoint(tempStart, imagesObj);
      let end3d = getPoint3dFromImagePoint(tempEnd, imagesObj);
      let distance = start3d.distanceTo(end3d);
      if (imageLine.isNerve) {
        cutImage.nerveDistanceP = distance;
        cutImage.nerveDistanceTextP = distance.toFixed(2) + " mm";
        cutImage.nervePointP = start3d;
      } else {
        cutImage.topDistanceP = distance;
        cutImage.topDistanceTextP = distance.toFixed(2) + " mm";
        cutImage.topPoint1P = start3d;
        cutImage.topPoint2P = end3d;

        if (childLine) {
          let childKonvaLine = childLine.konvaLine;
          tempStart = {
            x: childKonvaLine.anchor1.x() / stageWidth,
            y: childKonvaLine.anchor1.y() / stageHeight,
          };
          tempEnd = {
            x: childKonvaLine.anchor2.x() / stageWidth,
            y: childKonvaLine.anchor2.y() / stageHeight,
          };

          start3d = getPoint3dFromImagePoint(tempStart, imagesObj);
          end3d = getPoint3dFromImagePoint(tempEnd, imagesObj);
          distance = cutImage.nerveDistanceP = start3d.distanceTo(end3d);
          cutImage.nerveDistanceTextP = distance.toFixed(2) + " mm";
        }
      }

      if (konvaStage.isOriginal) {
        saveKonvaChanges(konvaStage, cutImage);
      }
    }
  } else {
    konvaLine.stroke(color);
    konvaLine.anchor1.fill(color);
    konvaLine.anchor2.fill(color);
    // konvaLine.anchor2.draggable(
    //   !imageLine.isNerve || cutContext.nerveLineLogic.index == 2,
    // );
    // konvaLine.draggable(
    //   imageLine.isNerve && cutContext.nerveLineLogic.index == 2,
    // );

    konvaLine.points([p1.x, p1.y, p2.x, p2.y]);
    konvaLine.anchor1.x(p1.x);
    konvaLine.anchor1.y(p1.y);
    konvaLine.anchor2.x(p2.x);
    konvaLine.anchor2.y(p2.y);

    konvaLine.anchor1.radius(anchorRadius);
    konvaLine.anchor1.radius(anchorRadius);
    konvaLine.anchor2.radius(anchorRadius);
    konvaLine.anchor2.radius(anchorRadius);
  }
  konvaLayer.draw();
  return konvaLine;
}

function saveKonvaChanges(konvaStage, cutImage, fromPopup) {
  const stageWidth = konvaStage.width();
  const stageHeight = konvaStage.height();
  let lines = fromPopup
    ? cutImage.imagesObj.popupLines
    : cutImage.imagesObj.lines;
  for (const imageLine of lines) {
    let konvaLine = imageLine.konvaLine;
    imageLine.start.x = konvaLine.anchor1.x() / stageWidth;
    imageLine.start.y = konvaLine.anchor1.y() / stageHeight;
    imageLine.end.x = konvaLine.anchor2.x() / stageWidth;
    imageLine.end.y = konvaLine.anchor2.y() / stageHeight;
  }

  if (cutImage.nerveDistanceP) {
    cutImage.nerveDistance = cutImage.nerveDistanceP;
    cutImage.nerveDistanceText = cutImage.nerveDistanceTextP;
  }
  if (cutImage.topDistanceP) {
    cutImage.topDistance = cutImage.topDistanceP;
    cutImage.topDistanceText = cutImage.topDistanceTextP;
  }
  if (cutImage.topPoint1P) {
    cutImage.topPoint1 = cutImage.topPoint1P;
    cutImage.topPoint2 = cutImage.topPoint2P;
    cutImage.topMidPoint = new THREE.Vector3(
      (cutImage.topPoint1.x + cutImage.topPoint2.x) / 2,
      (cutImage.topPoint1.y + cutImage.topPoint2.y) / 2,
      (cutImage.topPoint1.z + cutImage.topPoint2.z) / 2,
    );
  }
  if (cutImage.nervePointP) cutImage.nervePoint = cutImage.nervePointP;

  if (fromPopup) {
    drawLinesOnKonva(cutImage, cutImage.konvaStage, cutImage.konvaLayer, true);
  }
  // cutImage.overlayImage = getCutImageData(cutImage);
  // let lines = cutImage.imagesObj.lines;
  // for (const imageLine of lines) {
  //   const konvaLine = imageLine.konvaLine;
  // }
}

function addVerticalLineToSelectedImage() {
  let cutImage = cutContext.selectedImage;
  let imagesObj = cutImage.imagesObj;
  let konvaStage = cutContext.selectedStage;
  let konvaLayer = cutContext.selectedLayer;
  let clickPoint = cutContext.clickPoint;

  const stageWidth = konvaStage.width();
  const stageHeight = konvaStage.height();

  console.log(
    "addVerticalLineToSelectedImage",
    clickPoint.x,
    stageWidth,
    clickPoint.y,
    stageHeight,
  );
  let startPoint = {
    x: clickPoint.x / stageWidth,
    y: clickPoint.y / stageHeight,
  };
  startPoint.worldPos = getDwvPosFromImagePoint(startPoint, imagesObj);

  let lines = cutImage.imagesObj.popupLines;
  let horizontalLine = lines.find((x) => !x.isNerve);
  let endPoint = horizontalLine
    ? {
        x: (horizontalLine.start.x + horizontalLine.end.x) / 2,
        y: (horizontalLine.start.y + horizontalLine.end.y) / 2,
      }
    : {
        x: clickPoint.x / stageWidth,
        y: clickPoint.y / stageHeight + 0.2,
      };
  endPoint.worldPos = getDwvPosFromImagePoint(startPoint, imagesObj);

  cutImage.imagesObj.lines.push({
    dwvLine: null, // ?
    isNerve: true,
    start: startPoint,
    end: endPoint,
  });

  cutImage.imagesObj.popupLines.push({
    dwvLine: null, // ?
    isNerve: true,
    start: startPoint,
    end: endPoint,
  });
  drawLinesOnKonva(cutImage, konvaStage, konvaLayer, true);
  // let lines = konvaStage.isPopup
  //   ? cutImage.imagesObj.popupLines
  //   : cutImage.imagesObj.lines;
}

//#endregion

//#region Help

function getNextOpPoint(points, point1, minRange, maxRange, isUpper) {
  let minPoint = null;
  let lessAngle = Infinity;
  for (const p of points) {
    if (p != point1) {
      let distance = p.distanceTo(point1);
      let angle = getAngleBetweenPoints(
        point1,
        p,
        new THREE.Vector3(point1.x, p.y, point1.z),
      );
      if (isUpper && point1.z > p.z) continue;

      if (distance > minRange && distance <= maxRange && angle < lessAngle) {
        lessAngle = angle;
        minPoint = p;
        // minY = p.y;
      }
    }
  }
  return minPoint;
}

/**
 * Get the angle at point B formed by points A, B, C.
 * @param {THREE.Vector3} a - First point
 * @param {THREE.Vector3} b - Vertex point (angle corner)
 * @param {THREE.Vector3} c - Third point
 * @returns {number} Angle in radians
 */
function getAngleBetweenPoints(a, b, c) {
  const ba = new THREE.Vector3().subVectors(a, b); // vector from B to A
  const bc = new THREE.Vector3().subVectors(c, b); // vector from B to C
  return Math.abs(ba.angleTo(bc));
}

function getNextHighestPoint(points, point1, minRange) {
  let maxY = -Infinity;
  let maxPoint = null;
  for (const p of points) {
    if (p != point1 && p.y > maxY) {
      let distance = p.distanceTo(point1);
      if (distance > minRange) {
        maxPoint = p;
        maxY = p.y;
      }
    }
  }

  if (!maxPoint) {
    return getNextHighestPoint(points, point1, minRange * 0.9);
  }
  return maxPoint;
}

function getOpCrossPoint(crossMesh, startPoint, targetPoint) {
  let opPoint = null;
  let direction = targetPoint.clone();
  direction.subVectors(direction, startPoint);
  direction.normalize();
  cutRaycaster.set(startPoint, direction);
  let tempSide = crossMesh.material.side;
  crossMesh.material.side = THREE.DoubleSide;
  let maxDistance = 0;
  let intersects = cutRaycaster.intersectObject(crossMesh, true);
  // if (!intersects || !intersects.length) {
  //   direction = direction.negate();
  //   cutRaycaster.set(startPoint, direction);
  //   intersects = cutRaycaster.intersectObject(crossMesh);
  // }

  for (const intersect of intersects) {
    let distance = intersect.point.distanceTo(startPoint);
    // drawPoint(intersect.point);
    if (distance > maxDistance) {
      opPoint = intersect.point;
      maxDistance = distance;
    }
  }
  crossMesh.material.side = tempSide;
  return opPoint;
}

function getPerpendicularLineFromCenter(start, end, length, planeNormal) {
  if (start && end) {
    // planeNormal = new THREE.Vector3(1, 0, 0),
    // const plane = new THREE.Plane();
    // plane.setFromCoplanarPoints(start, end, maxPoint);
    // const planeNormal = plane.normal.clone();

    // 1. Midpoint
    const mid = start.clone().add(end).multiplyScalar(0.5);

    // 2. Direction of original line
    const dir = start.clone().sub(end).normalize();

    // Handle edge case: if dir is parallel to planeNormal, cross product = 0
    let ref = planeNormal.clone();
    if (Math.abs(dir.dot(ref)) > 0.999) {
      ref.set(0, 0, 1); // fallback to Z axis
    }

    // 3. Perpendicular direction via cross product
    const perpDir = dir.clone().cross(ref).normalize();

    // 4. Create perpendicular line geometry
    const perpEnd = mid.clone().add(perpDir.clone().multiplyScalar(length));
    // const geometry = new THREE.BufferGeometry().setFromPoints([mid, perpEnd]);
    // const material = new THREE.LineBasicMaterial({ color: 0x00ff00 });
    // let line = new THREE.Line(geometry, material);
    const line = {};
    line.start = mid;
    line.end = perpEnd;
    return line;
  }
}

function getTopCrossPoint(
  crossMeshes,
  meshPoint,
  maxDiffY,
  crossPoints,
  targetPoint,
) {
  let opPoint = null;
  let direction = targetPoint;
  if (!direction)
    direction = new THREE.Vector3(meshPoint.x, meshPoint.y + 10, meshPoint.z);
  direction.subVectors(direction, meshPoint);
  direction.normalize();
  cutRaycaster.set(meshPoint, direction);
  let maxDistance = 0;
  let lastCrossPoint = crossPoints[crossPoints.length - 1];
  let lastCrossDistance = Math.abs(lastCrossPoint.y - meshPoint.y);

  let intersects = cutRaycaster.intersectObjects(crossMeshes);
  // while (intersects && intersects.length > 0) {
  //   cutRaycaster.set(opPoint, direction);
  //   intersects = cutRaycaster.intersectObject(mesh);
  // }
  for (const intersect of intersects) {
    let distance = intersect.point.distanceTo(meshPoint);
    if (distance > maxDistance && distance < maxDiffY) {
      opPoint = intersect.point;
      maxDistance = distance;
    }
  }

  if (maxDistance < lastCrossDistance * 0.3) {
    opPoint = new THREE.Vector3(meshPoint.x, lastCrossPoint.y, meshPoint.z);
  }

  return opPoint;
}

function getMaxCrossPoint(mesh, dirPoint, meshPoint, box) {
  let opPoint = null;

  let direction = new THREE.Vector3(dirPoint.x, meshPoint.y, dirPoint.z);
  direction.subVectors(direction, meshPoint);
  direction.normalize();
  cutRaycaster.set(meshPoint, direction);
  let intersects1 = cutRaycaster.intersectObject(mesh);

  direction = direction.negate();
  cutRaycaster.set(meshPoint, direction);
  let intersects2 = cutRaycaster.intersectObject(mesh);

  let intersects = intersects1.concat(intersects2);
  let maxDistance = 0;
  for (const intersect of intersects) {
    if (box.containsPoint(intersect.point)) {
      let distance = intersect.distance; // getDistance(intersect.point, meshPoint); // intersect.distance; //
      if (distance > maxDistance) {
        opPoint = intersect.point;
        maxDistance = distance;
      }
    }
  }

  return { meshPoint: meshPoint, opPoint: opPoint, distance: maxDistance };
}

function getMatrixAroundPoint(matrix, pivot) {
  // Create a transformation matrix that includes the pivot translation
  const tempMatrix = new THREE.Matrix4();

  // Compose: translate to origin -> transform -> translate back
  tempMatrix.makeTranslation(-pivot.x, -pivot.y, -pivot.z);
  tempMatrix.premultiply(matrix); // premultiply multiply
  tempMatrix.premultiply(
    new THREE.Matrix4().makeTranslation(pivot.x, pivot.y, pivot.z),
  );

  return tempMatrix;
}

function getMeshesBox(isMaxilla, isFull) {
  let min = { x: 100000, y: 100000, z: 100000 };
  let max = { x: -10000, y: -100000, z: -100000 };
  let teethBox = { min, max };
  vueApp.allItems.forEach(function (model) {
    if (
      (model.isTooth && model.isMaxilla == isMaxilla) ||
      (!isMaxilla && model.isNerve) ||
      (isFull && model.isBone && model.isMaxilla == isMaxilla)
    ) {
      let mesh = viewer.get_model_mesh(model.id);
      let geometry = mesh.geometry;
      if (!geometry) return;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      let toothBox = geometry.boundingBox;
      toothBox = offsetBox(
        toothBox,
        mesh.position.x,
        mesh.position.y,
        mesh.position.z,
      );

      if (toothBox.min.x < teethBox.min.x) {
        teethBox.min.x = toothBox.min.x;
      }
      if (toothBox.min.y < teethBox.min.y) {
        teethBox.min.y = toothBox.min.y;
      }
      if (toothBox.min.z < teethBox.min.z) {
        teethBox.min.z = toothBox.min.z;
      }
      if (toothBox.max.x > teethBox.max.x) {
        teethBox.max.x = toothBox.max.x;
      }
      if (toothBox.max.y > teethBox.max.y) {
        teethBox.max.y = toothBox.max.y;
      }
      if (toothBox.max.z > teethBox.max.z) {
        teethBox.max.z = toothBox.max.z;
      }
    }
  });
  teethBox = offsetBox(teethBox, 0, 0, 0, 0);
  return teethBox;
}

function getHalfBox(baseBox, isLeft) {
  const minX = baseBox.min.x;
  const maxX = baseBox.max.x;
  const minY = baseBox.min.y;
  const maxY = baseBox.max.y;
  const minZ = baseBox.min.z;
  const maxZ = baseBox.max.z;

  const sizeX = maxX - minX;
  const sizeY = maxY - minY;
  const sizeZ = maxZ - minZ;

  const min = isLeft
    ? new THREE.Vector3(minX, minY, minZ)
    : new THREE.Vector3(minX + sizeX / 2, minY, minZ);
  const max = isLeft
    ? new THREE.Vector3(minX + sizeX / 2, maxY, maxZ)
    : new THREE.Vector3(maxX, maxY, maxZ);
  const size = new THREE.Vector3(sizeX / 2, sizeY, sizeZ);
  const center = new THREE.Vector3(
    min.x + size.x / 2,
    min.y + size.y,
    min.z + size.z,
  );
  return { min, max, center, size };
}

function getMidYPoint(points) {
  if (points && points.length) {
    let minY = Infinity;
    let maxY = -Infinity;
    let minPoint = null;
    let maxPoint = null;
    for (const point of points) {
      if (point.y < minY) {
        minY = point.y;
        minPoint = point;
      }
      if (point.y > maxY) {
        maxY = point.y;
        maxPoint = point;
      }
    }
    if (minPoint && maxPoint) {
      let midPoint = new THREE.Vector3(
        (minPoint.x + maxPoint.x) / 2,
        (minPoint.y + maxPoint.y) / 2,
        (minPoint.z + maxPoint.z) / 2,
      );
      return midPoint;
    }
  }
}

// !!!
function getTopPointsObj(points, minDistance, isUpper) {
  if (points && points.length) {
    let p1 = null;
    let p2 = null;

    let teetBox = isUpper ? maxillaBox : mandilaBox;
    let endY = isUpper ? teetBox.max.y : teetBox.min.y;

    let startPoint = points[0];
    let endPoint = points[points.length - 1];
    let startY = startPoint.y;
    endY = isUpper ? Math.min(endPoint.y, endY) : Math.max(endPoint.y, endY);
    const step = isUpper ? -2 : 2;
    let maxDistSq = 0;

    for (let y = startY; isUpper ? y <= endY : y >= endY; y -= step) {
      const lPoints = isUpper
        ? points.filter((p) => p.y >= y && p.y < y - step)
        : points.filter((p) => p.y <= y && p.y > y - step);
      for (let i = 0; i < lPoints.length; i++) {
        for (let j = i + 1; j < lPoints.length; j++) {
          const dSq = lPoints[i].distanceToSquared(lPoints[j]);
          if (dSq > maxDistSq) {
            maxDistSq = dSq;
            p1 = lPoints[i];
            p2 = lPoints[j];
          }
        }
      }

      if (Math.sqrt(maxDistSq) >= minDistance) {
        break;
      }
    }

    if (!p1) {
      console.log("!!! getTopPointsObj strange bone");
      p1 = points[0];
      p2 = findFarestPoint(p1, points, minDistance);
      // p2 = points.find(
      //   (p) =>
      //     new THREE.Vector2(p.x, p.z).distanceTo(
      //       new THREE.Vector2(p1.x, p1.z),
      //     ) > minDistance,
      // );
    }

    const topPointsObj = {
      point1: p1,
      point2: p2,
      midPoint: null,
    };
    topPointsObj.midPoint = p2
      ? new THREE.Vector3(
          (p1.x + p2.x) / 2,
          (p1.y + p2.y) / 2,
          (p1.z + p2.z) / 2,
        )
      : p1;
    // drawPoint(topPointsObj.point1, 0x0000ff);
    // drawPoint(topPointsObj.point2, 0x00ff00);
    // drawPoint(topPointsObj.midPoint, 0xff0000);
    // drawLine(topPointsObj.point1, topPointsObj.point2);
    return topPointsObj;
  }
}

function findFarestPoint(p1, points, minDistance) {
  let p2 = null;
  while (!p2 && minDistance > 0.01) {
    p2 = points.find(
      (p) =>
        new THREE.Vector2(p.x, p.z).distanceTo(new THREE.Vector2(p1.x, p1.z)) >=
        minDistance,
    );
    minDistance /= 2;
  }
  return p2;
}

function getVerticalPlane(min, max, rotMatrix, pivotPoint) {
  // 1. Define the 4 corner points
  const v1 = new THREE.Vector3(min.x, min.y, min.z);
  const v2 = new THREE.Vector3(max.x, min.y, max.z);
  const v3 = new THREE.Vector3(max.x, max.y, max.z);
  const v4 = new THREE.Vector3(min.x, max.y, min.z);

  const plane = new THREE.Plane();
  plane.setFromCoplanarPoints(v1, v2, v3);
  // plane.min = min;
  // plane.max = max;
  plane.v1 = v1;
  plane.v2 = v2;
  plane.v3 = v3;
  plane.v4 = v4;

  if (rotMatrix && pivotPoint) {
    transformPlaneAroundPoint(plane, rotMatrix, pivotPoint);

    rotatePointAroundPivotMatrix(plane.v1, rotMatrix, pivotPoint);
    rotatePointAroundPivotMatrix(plane.v2, rotMatrix, pivotPoint);
    rotatePointAroundPivotMatrix(plane.v3, rotMatrix, pivotPoint);
    rotatePointAroundPivotMatrix(plane.v4, rotMatrix, pivotPoint);
    // plane.v2 = v2;
    // plane.v3 = v3;
    // plane.v4 = v4;
  }

  return plane;
}

function transformPlaneAroundPoint(plane, rotMatrix, pivotPoint) {
  const tempPlane = plane.clone();

  // Move plane to pivot point space
  const translationToPivot = new THREE.Vector3().subVectors(
    tempPlane.normal.clone().multiplyScalar(-tempPlane.constant),
    pivotPoint,
  );

  // Transform the normal
  tempPlane.normal.applyMatrix4(rotMatrix).normalize();

  // Transform a point on the plane and calculate new constant
  const transformedPoint = pivotPoint
    .clone()
    .add(translationToPivot.applyMatrix4(rotMatrix));

  tempPlane.constant = -transformedPoint.dot(tempPlane.normal);
  plane.copy(tempPlane);
  return plane;
}

export function rotatePointAroundPivotMatrix(point, matrix4, pivotPoint) {
  const mFinal = new THREE.Matrix4();
  const mOffset = new THREE.Matrix4();

  mFinal.makeTranslation(pivotPoint.x, pivotPoint.y, pivotPoint.z);

  mOffset.makeTranslation(-pivotPoint.x, -pivotPoint.y, -pivotPoint.z);

  mFinal.multiply(matrix4).multiply(mOffset);

  return point.applyMatrix4(mFinal);
}

function drawPlane(plane, color) {
  const min = plane.min;
  const max = plane.max;
  if (min && max) {
    const v1 = new THREE.Vector3(min.x, min.y, min.z);
    const v2 = new THREE.Vector3(max.x, min.y, max.z);
    const v3 = new THREE.Vector3(max.x, max.y, max.z);
    const v4 = new THREE.Vector3(min.x, max.y, min.z);

    drawPlaneFromPoints(v1, v2, v3, v4, color);
  } else {
    const v1 = plane.v1;
    const v2 = plane.v2;
    const v3 = plane.v3;
    const v4 = plane.v4;
    if (v1 && v2 && v3 && v4) {
      drawPlaneFromPoints(v1, v2, v3, v4, color);
    }
  }
}

let tempPlanes = [];
function drawPlaneFromPoints(v1, v2, v3, v4, color) {
  if (v1 && v2 && v3 && v4) {
    const geometry = new THREE.BufferGeometry();
    const vertices = new Float32Array([
      ...v1.toArray(),
      ...v2.toArray(),
      ...v4.toArray(), // Triangle 1 (v1, v2, v4)
      ...v2.toArray(),
      ...v3.toArray(),
      ...v4.toArray(), // Triangle 2 (v2, v3, v4)
    ]);
    geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
    geometry.computeVertexNormals(); // Useful for lighting

    const material = new THREE.MeshBasicMaterial({
      color: color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.5,
    });
    const plane = new THREE.Mesh(geometry, material);
    // viewer.scene.add(plane);

    const lgeometry = new THREE.BufferGeometry().setFromPoints([
      v1,
      v2,
      v3,
      v4,
    ]);
    const lmaterial = new THREE.LineBasicMaterial({ color: color });
    const rectangleOutline = new THREE.LineLoop(lgeometry, lmaterial);
    tempPlanes.push(rectangleOutline);
    viewer.scene.add(rectangleOutline);
  }
}

//#region checkLineInRectangle

/**
 * Check if a line segment is fully inside a rectangle.
 * Returns the cross point if NOT fully inside, or null if fully inside / no intersection.
 *
 * @param {THREE.Vector3} lineP1 - Start point of the line
 * @param {THREE.Vector3} lineP2 - End point of the line
 * @param {THREE.Vector3[]} rectPoints - 4 corners of the rectangle IN ORDER (CW or CCW)
 * @returns {{ isInside: boolean, crossPoint: THREE.Vector3|null }}
 */
function checkLineInRectangle(lineP1, lineP2, rectPoints) {
  const [r0, r1, r2, r3] = rectPoints;

  // --- Build rectangle local coordinate system ---
  const u = new THREE.Vector3().subVectors(r1, r0); // width direction
  const v = new THREE.Vector3().subVectors(r3, r0); // height direction
  const normal = new THREE.Vector3().crossVectors(u, v);
  const normalLen = normal.length();

  if (normalLen < 1e-6) {
    console.warn("Degenerate rectangle (collinear points)");
    return { isInside: false, crossPoint: null };
  }

  normal.normalize();
  const uLen = u.length();
  const vLen = v.length();
  u.normalize();
  v.normalize();

  // --- Line direction ---
  const lineDir = new THREE.Vector3().subVectors(lineP2, lineP1);
  const lineLen = lineDir.length();

  if (lineLen < 1e-6) {
    // Line is a single point
    const inside = isPointInRect3D(lineP1, r0, u, v, uLen, vLen, normal);
    return { isInside: inside, crossPoint: null };
  }

  // --- Check if line is parallel to the rectangle plane ---
  const denom = normal.dot(lineDir);

  if (Math.abs(denom) < 1e-6) {
    // Parallel — check if coplanar
    const dist = normal.dot(new THREE.Vector3().subVectors(lineP1, r0));
    if (Math.abs(dist) > 1e-6) {
      return { isInside: false, crossPoint: null }; // parallel but not on plane
    }
    // Coplanar case
    return handleCoplanarCase(lineP1, lineP2, r0, u, v, uLen, vLen);
  }

  // --- Non-coplanar: line-plane intersection ---
  const t = normal.dot(new THREE.Vector3().subVectors(r0, lineP1)) / denom;

  if (t < 0 || t > 1) {
    return { isInside: false, crossPoint: null }; // outside segment range
  }

  const intersectPoint = new THREE.Vector3().addVectors(
    lineP1,
    lineDir.clone().multiplyScalar(t),
  );

  if (isPointInRect3D(intersectPoint, r0, u, v, uLen, vLen, normal)) {
    return { isInside: false, crossPoint: intersectPoint };
  }

  return { isInside: false, crossPoint: null };
}

// ---------- Helpers ----------

function isPointInRect3D(point, r0, u, v, uLen, vLen, normal) {
  // Must lie on the plane
  const dist = normal.dot(new THREE.Vector3().subVectors(point, r0));
  if (Math.abs(dist) > 1e-4) return false;

  // Project onto local axes
  const toPoint = new THREE.Vector3().subVectors(point, r0);
  const projU = toPoint.dot(u);
  const projV = toPoint.dot(v);

  return (
    projU >= -1e-6 &&
    projU <= uLen + 1e-6 &&
    projV >= -1e-6 &&
    projV <= vLen + 1e-6
  );
}

function handleCoplanarCase(lineP1, lineP2, r0, u, v, uLen, vLen) {
  // Project to 2D rectangle-local space
  const to2D = (p) => {
    const toP = new THREE.Vector3().subVectors(p, r0);
    return new THREE.Vector2(toP.dot(u), toP.dot(v));
  };

  const l1 = to2D(lineP1);
  const l2 = to2D(lineP2);

  const p1Inside = isInside2D(l1, uLen, vLen);
  const p2Inside = isInside2D(l2, uLen, vLen);

  // ✅ Fully inside
  if (p1Inside && p2Inside) {
    return { isInside: true, crossPoint: null };
  }

  // Find intersection with rectangle edges
  const edges = [
    [new THREE.Vector2(0, 0), new THREE.Vector2(uLen, 0)],
    [new THREE.Vector2(uLen, 0), new THREE.Vector2(uLen, vLen)],
    [new THREE.Vector2(uLen, vLen), new THREE.Vector2(0, vLen)],
    [new THREE.Vector2(0, vLen), new THREE.Vector2(0, 0)],
  ];

  for (const [e1, e2] of edges) {
    const hit = segmentIntersect2D(l1, l2, e1, e2);
    if (hit) {
      // Convert 2D hit back to 3D
      const crossPoint = new THREE.Vector3()
        .copy(r0)
        .addScaledVector(u, hit.x)
        .addScaledVector(v, hit.y);
      return { isInside: false, crossPoint };
    }
  }

  return { isInside: false, crossPoint: null };
}

function isInside2D(p, uLen, vLen) {
  return (
    p.x >= -1e-6 && p.x <= uLen + 1e-6 && p.y >= -1e-6 && p.y <= vLen + 1e-6
  );
}

function segmentIntersect2D(p1, p2, p3, p4) {
  const d1x = p2.x - p1.x,
    d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x,
    d2y = p4.y - p3.y;

  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-8) return null; // parallel

  const d3x = p3.x - p1.x,
    d3y = p3.y - p1.y;
  const t = (d3x * d2y - d3y * d2x) / cross;
  const s = (d3x * d1y - d3y * d1x) / cross;

  if (t >= -1e-6 && t <= 1 + 1e-6 && s >= -1e-6 && s <= 1 + 1e-6) {
    return new THREE.Vector2(p1.x + t * d1x, p1.y + t * d1y);
  }
  return null;
}

//#endregion

//#endregion

export {
  fetchCoordMappings,
  showDicomCutReportDlg,
  saveCutsReport,
  fillCutImages,
  drawLinesOnKonva,
  saveKonvaChanges,
  saveSettings,
  addVerticalLineToSelectedImage,
  showReportOptions,
  showCutReportsListDlg,
  removePdfReport,
  showEditPopup,
  globalKonvaStage,
  globalKonvaLayer,
};
